import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { fetchBoot } from "../lib/boot";
import { applyTheme } from "../lib/theme";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const hasToken = () => !!localStorage.getItem("plannio_token");
  const [user, setUser] = useState(() => (hasToken() ? null : false));
  const [loading, setLoading] = useState(() => hasToken());

  const refresh = async () => {
    const tokenAtStart = localStorage.getItem("plannio_token");
    if (!tokenAtStart) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      // One consolidated request boots the whole shell (user + widgets primed into the GET
      // cache). Falls back to the classic /auth/me if boot is unavailable for any reason.
      let me;
      try {
        me = (await fetchBoot()).user;
      } catch {
        me = (await api.get("/auth/me", { timeout: 8000 })).data;
      }
      setUser(me);
    } catch {
      // Only clear if the token hasn't been replaced mid-flight (e.g. a fresh OAuth token
      // written by completeOAuth while this stale-token request was still pending).
      if (localStorage.getItem("plannio_token") === tokenAtStart) {
        localStorage.removeItem("plannio_token");
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // On first load on a device with no local theme, honour the account's saved theme.
  useEffect(() => {
    if (user && user.settings?.theme && !localStorage.getItem("plannio_theme")) {
      applyTheme(user.settings.theme);
    }
  }, [user]);

  const handleAuth = async (data) => {
    if (data.token) localStorage.setItem("plannio_token", data.token);
    // Boot BEFORE rendering the authenticated shell: one consolidated request returns the full
    // /auth/me projection and primes every shell widget's GET cache. Setting the user first
    // (as before) made components mount and fire ten individual requests while boot was still
    // in flight, recreating the waterfall this replaces.
    try {
      const boot = await fetchBoot();
      setUser(boot.user);
    } catch (e) {
      setUser(data); // slim fallback keeps login working if boot is unavailable
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
      } catch { /* keep the slim payload; boot will run on next mount */ }
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.removeItem("plannio_dismissed_announcements");
    await handleAuth(data);
    return data;
  };

  const register = async (name, email, password, plan = "free", billing_cycle = "monthly", extra = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, plan, billing_cycle, ...extra });
    localStorage.setItem("plannio_new_user", "1");
    if (!data.email_verification_required) await handleAuth(data);
    return data;
  };

  const verifyEmail = async (token_hash) => {
    const { data } = await api.post("/auth/verify-email", { token_hash });
    if (data.token) await handleAuth(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("plannio_token");
    setUser(false);
  };

  const updateUser = (patch) => setUser((u) => ({ ...u, ...patch }));

  const completeOAuth = (data) => {
    localStorage.removeItem("plannio_dismissed_announcements");
    handleAuth(data);
    return data;
  };

  const startImpersonation = async (uid) => {
    const { data } = await api.post(`/admin/users/${uid}/impersonate`);
    if (data.token) localStorage.setItem("plannio_token", data.token);
    window.location.href = "/app/dashboard";
  };

  const stopImpersonation = async () => {
    try {
      const { data } = await api.post(`/auth/stop-impersonation`);
      if (data.token) localStorage.setItem("plannio_token", data.token);
    } catch {}
    window.location.href = "/admin/users";
  };

  const impersonating = user?.is_impersonating ? user.email : null;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyEmail, logout, refresh, updateUser,
      completeOAuth, startImpersonation, stopImpersonation, impersonating }}>
      {children}
    </AuthContext.Provider>
  );
}
